import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  Pagination,
  useTheme,
  useMediaQuery,
  TextField,
  Chip,
} from '@mui/material';
import { Task, UserData } from '../../types/todoTypes';
import { listenToTasksByUser } from '../../services/todoService';
import { getUsers } from '../../services/authService';
import TaskCard from './TaskCard';

interface UserTaskContainerProps {
  containerTitle: string;
  containerNumber: number; // 1 or 2
  onTaskClick: (task: Task) => void;
  onTaskDelete: (task: Task) => void;
}

const UserTaskContainer: React.FC<UserTaskContainerProps> = ({
  containerTitle,
  containerNumber,
  onTaskClick,
  onTaskDelete,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Calculate responsive page size
  const pageSize = isMobile ? 6 : isTablet ? 8 : 10;

  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [users, setUsers] = useState<UserData[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<string>('');

  // LocalStorage keys for persistence
  const STORAGE_KEY_USER = `todo_container${containerNumber}_user`;
  const STORAGE_KEY_STATUS = `todo_container${containerNumber}_status`;
  const STORAGE_KEY_DATE = `todo_container${containerNumber}_date`;

  // Load users on mount
  useEffect(() => {
    loadUsers();
    // Restore saved filters from localStorage
    const savedUserId = localStorage.getItem(STORAGE_KEY_USER);
    const savedStatus = localStorage.getItem(STORAGE_KEY_STATUS);
    const savedDate = localStorage.getItem(STORAGE_KEY_DATE);

    if (savedUserId) setSelectedUserId(savedUserId);
    if (savedStatus) setSelectedStatus(savedStatus);
    if (savedDate) setSelectedDate(savedDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save filters to localStorage
  useEffect(() => {
    if (selectedUserId) {
      localStorage.setItem(STORAGE_KEY_USER, selectedUserId);
    }
  }, [selectedUserId, STORAGE_KEY_USER]);

  useEffect(() => {
    if (selectedStatus) {
      localStorage.setItem(STORAGE_KEY_STATUS, selectedStatus);
    } else {
      localStorage.removeItem(STORAGE_KEY_STATUS);
    }
  }, [selectedStatus, STORAGE_KEY_STATUS]);

  useEffect(() => {
    if (selectedDate) {
      localStorage.setItem(STORAGE_KEY_DATE, selectedDate);
    } else {
      localStorage.removeItem(STORAGE_KEY_DATE);
    }
  }, [selectedDate, STORAGE_KEY_DATE]);

  // Real-time listener for tasks
  useEffect(() => {
    if (!selectedUserId) {
      setTasks([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setCurrentPage(1);

    const unsubscribe = listenToTasksByUser(
      selectedUserId,
      100, // Fetch all tasks for pagination
      (fetchedTasks) => {
        setTasks(fetchedTasks);
        setLoading(false);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [selectedUserId]);

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const fetchedUsers = await getUsers('', 100);
      setUsers(fetchedUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleUserChange = (event: any) => {
    setSelectedUserId(event.target.value);
    setCurrentPage(1);
  };

  const handleStatusChange = (event: any) => {
    setSelectedStatus(event.target.value);
    setCurrentPage(1);
  };

  const handleDateChange = (event: any) => {
    setSelectedDate(event.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  // Filter tasks based on status and date
  const filteredTasks = useMemo(() => {
    let result = [...tasks];

    // Filter by status
    if (selectedStatus) {
      result = result.filter((task) => task.status === selectedStatus);
    }

    // Filter by date
    if (selectedDate) {
      const filterDate = new Date(selectedDate);
      filterDate.setHours(0, 0, 0, 0);

      result = result.filter((task) => {
        const taskDate = task.createdAt.toDate();
        taskDate.setHours(0, 0, 0, 0);
        return taskDate.getTime() === filterDate.getTime();
      });
    }

    return result;
  }, [tasks, selectedStatus, selectedDate]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredTasks.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedTasks = filteredTasks.slice(startIndex, endIndex);

  return (
    <Paper
      elevation={2}
      sx={{
        p: 3,
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header with filters */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {containerTitle}
        </Typography>

        {/* User selector */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id={`user-select-${containerNumber}-label`}>
            Select User
          </InputLabel>
          <Select
            labelId={`user-select-${containerNumber}-label`}
            value={selectedUserId}
            onChange={handleUserChange}
            label="Select User"
            disabled={loadingUsers}
          >
            {loadingUsers ? (
              <MenuItem disabled>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Loading users...
              </MenuItem>
            ) : users.length === 0 ? (
              <MenuItem disabled>No users available</MenuItem>
            ) : (
              users.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.name} ({user.email})
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>

        {/* Filters row */}
        {selectedUserId && (
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            {/* Status filter */}
            <FormControl sx={{ minWidth: 200, maxWidth: 250 }}>
              <InputLabel id={`status-filter-${containerNumber}-label`} shrink>
                Status
              </InputLabel>
              <Select
                labelId={`status-filter-${containerNumber}-label`}
                value={selectedStatus}
                onChange={handleStatusChange}
                label="Status"
                displayEmpty
                notched
              >
                <MenuItem value="">
                  <em>All Statuses</em>
                </MenuItem>
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="In Progress">In Progress</MenuItem>
                <MenuItem value="Blocked">Blocked</MenuItem>
                <MenuItem value="Complete">Complete</MenuItem>
              </Select>
            </FormControl>

            {/* Date filter */}
            <TextField
              type="date"
              label="Filter by Date"
              value={selectedDate}
              onChange={handleDateChange}
              sx={{ minWidth: 200, maxWidth: 250 }}
              InputLabelProps={{
                shrink: true,
              }}
            />

            {/* Active filters display */}
            {(selectedStatus || selectedDate) && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', flexGrow: 1 }}>
                {selectedStatus && (
                  <Chip
                    label={`Status: ${selectedStatus}`}
                    onDelete={() => setSelectedStatus('')}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
                {selectedDate && (
                  <Chip
                    label={`Date: ${new Date(selectedDate).toLocaleDateString()}`}
                    onDelete={() => setSelectedDate('')}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Task grid */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
        {!selectedUserId ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flex: 1,
            }}
          >
            <Typography variant="body1" color="text.secondary">
              Select a user to view their tasks
            </Typography>
          </Box>
        ) : loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flex: 1,
            }}
          >
            <CircularProgress />
          </Box>
        ) : paginatedTasks.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flex: 1,
            }}
          >
            <Typography variant="body1" color="text.secondary">
              No tasks found for this user
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {paginatedTasks.map((task) => (
              <Grid item xs={6} key={task.id}>
                <TaskCard
                  task={task}
                  onClick={onTaskClick}
                  onDelete={onTaskDelete}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* Task count */}
      {selectedUserId && !loading && (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Showing {paginatedTasks.length} of {filteredTasks.length} task
            {filteredTasks.length !== 1 ? 's' : ''}
            {(selectedStatus || selectedDate) && ` (filtered from ${tasks.length} total)`}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default UserTaskContainer;
